<svelte:options runes={false} />
<script>
  // U5/KTD4 — the header ORG multi-select (Strategy + Portfolio, Paper & Live). Modeled on SymbolMultiSelect
  // (chips, click-to-open dropdown, runes={false} REASSIGN-not-mutate reactivity, a11y), but:
  //   - NO search input (participating-org lists are small — 2-5; a combobox would be noise).
  //   - A >=1 GUARD: the × on the LAST remaining chip is rendered DISABLED (aria-disabled + tooltip), never
  //     hidden (avoids layout shift) and never a silent no-op.
  //   - LOADING / ERROR / EMPTY states so the header never renders a blank control while the orgs fetch.
  // `selected` is the single source of truth (an array of org ids), two-way bound from the parent; every
  // add/remove builds a FRESH array so the bind propagates in legacy mode.
  import { onDestroy } from 'svelte';

  export let selected = [];                 // selected org ids (>=1 enforced)
  export let available = [];                // [{ orgId, orgName }] the caller may select (server-filtered)
  export let state = 'ready';               // 'loading' | 'error' | 'ready'
  export let disabled = false;
  export let onRetry = null;                // called when the user clicks "retry" in the error state
  export let id = 'org-multiselect';

  let open = false;
  let rootEl;

  const labelFor = (orgId) => available.find((o) => o.orgId === orgId)?.orgName || orgId;

  $: selectedSet = new Set(selected);
  $: allSelected = available.length > 0 && selected.length >= available.length;
  $: isLastSelected = selected.length <= 1;   // the >=1 guard: cannot remove the final chip

  function toggle(orgId) {
    if (disabled) return;
    if (selectedSet.has(orgId)) {
      remove(orgId);
    } else {
      selected = [...selected, orgId];
    }
  }

  function remove(orgId) {
    if (disabled) return;
    if (selected.length <= 1) return;        // >=1 invariant — never remove the last
    selected = selected.filter((o) => o !== orgId);
  }

  function selectAll() {
    selected = available.map((o) => o.orgId);
  }

  function openDropdown() {
    if (disabled || state !== 'ready') return;
    open = true;
  }
  function closeDropdown() {
    open = false;
  }

  function onWindowPointerDown(event) {
    if (rootEl && !rootEl.contains(event.target)) closeDropdown();
  }
  $: if (typeof window !== 'undefined') {
    if (open) window.addEventListener('pointerdown', onWindowPointerDown, true);
    else window.removeEventListener('pointerdown', onWindowPointerDown, true);
  }
  onDestroy(() => {
    if (typeof window !== 'undefined') window.removeEventListener('pointerdown', onWindowPointerDown, true);
  });
</script>

<div class="oms-root" class:disabled bind:this={rootEl}>
  {#if state === 'loading'}
    <span class="oms-chip oms-placeholder" aria-busy="true">Loading orgs…</span>
  {:else if state === 'error'}
    <span class="oms-error">
      Failed to load orgs
      {#if onRetry}<button type="button" class="oms-retry" on:click={onRetry}>retry</button>{/if}
    </span>
  {:else if available.length === 0}
    <span class="oms-chip oms-placeholder">No orgs available</span>
  {:else}
    <div class="oms-field" class:open role="presentation" on:click={openDropdown}>
      <span class="oms-prefix">Orgs:</span>
      {#each selected as orgId (orgId)}
        <span class="oms-chip">
          <span class="oms-chip-label">{labelFor(orgId)}</span>
          <button
            type="button"
            class="oms-chip-remove"
            class:disabled={isLastSelected}
            aria-disabled={isLastSelected}
            aria-label={isLastSelected ? 'At least one org must remain selected' : `Remove ${labelFor(orgId)}`}
            title={isLastSelected ? 'At least one org must remain selected' : `Remove ${labelFor(orgId)}`}
            disabled={isLastSelected || disabled}
            on:click|stopPropagation={() => remove(orgId)}
          >×</button>
        </span>
      {/each}
      {#if !allSelected}
        <span class="oms-subset-indicator" title="Showing a subset of orgs">{selected.length} of {available.length} orgs</span>
      {/if}
      <button type="button" class="oms-caret" aria-label="Choose orgs" on:click|stopPropagation={() => (open ? closeDropdown() : openDropdown())}>▾</button>
    </div>

    {#if open}
      <div class="oms-dropdown" role="listbox" aria-multiselectable="true" {id}>
        <div class="oms-dropdown-head">
          <span class="oms-count">{selected.length}/{available.length} selected</span>
          {#if !allSelected}<button type="button" class="oms-all" on:click={selectAll}>Select all</button>{/if}
        </div>
        {#each available as org (org.orgId)}
          {@const sel = selectedSet.has(org.orgId)}
          {@const lockedLast = sel && isLastSelected}
          <!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
          <div
            class="oms-option"
            class:selected={sel}
            class:disabled-row={lockedLast}
            role="option"
            aria-selected={sel}
            title={lockedLast ? 'At least one org must remain selected' : undefined}
            on:click={() => (lockedLast ? null : toggle(org.orgId))}
          >
            <span class="oms-check" aria-hidden="true">{sel ? '✓' : ''}</span>
            <span class="oms-option-label">{org.orgName || org.orgId}</span>
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</div>

<style>
  /* Uses the app's DARK theme tokens (theme.css): --bg-*, --text-*, --border-*, --color-primary. */
  .oms-root { position: relative; display: inline-flex; align-items: center; font-size: 0.85rem; }
  .oms-root.disabled { opacity: 0.6; pointer-events: none; }
  .oms-field {
    display: inline-flex; align-items: center; flex-wrap: wrap; gap: 0.35rem;
    padding: 0.2rem 0.4rem; border: 1px solid var(--border-primary, #1e1f22); border-radius: 6px;
    background: var(--bg-tertiary, #1a1b1e); cursor: pointer; max-width: 100%;
  }
  .oms-field.open { border-color: var(--color-primary, #00d4ff); }
  .oms-prefix { color: var(--text-secondary, #8a8b8d); font-weight: 600; margin-right: 0.1rem; }
  .oms-chip {
    display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.1rem 0.45rem;
    background: var(--color-primary-alpha-20, rgba(0, 212, 255, 0.2)); color: var(--text-primary, #ffffff);
    border-radius: 999px; white-space: nowrap;
  }
  .oms-chip-label { font-weight: 600; }
  .oms-chip-remove {
    border: none; background: transparent; color: inherit; cursor: pointer; font-size: 1rem; line-height: 1; padding: 0;
  }
  .oms-chip-remove.disabled { cursor: not-allowed; opacity: 0.4; }
  .oms-subset-indicator { color: var(--color-warning, #ffaa00); font-weight: 600; white-space: nowrap; }
  .oms-caret { border: none; background: transparent; cursor: pointer; color: var(--text-secondary, #8a8b8d); padding: 0 0.2rem; }
  .oms-placeholder { background: var(--bg-quaternary, #212226); color: var(--text-secondary, #8a8b8d); padding: 0.1rem 0.5rem; border-radius: 999px; }
  .oms-error { color: var(--color-danger, #ff3b3b); display: inline-flex; gap: 0.4rem; align-items: center; }
  .oms-retry { border: none; background: transparent; color: var(--color-primary, #00d4ff); cursor: pointer; text-decoration: underline; padding: 0; }
  .oms-dropdown {
    position: absolute; top: calc(100% + 4px); left: 0; z-index: 30; min-width: 200px;
    background: var(--bg-secondary, #111214); border: 1px solid var(--border-secondary, #2a2b2e); border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5); padding: 0.3rem;
  }
  .oms-dropdown-head { display: flex; justify-content: space-between; align-items: center; padding: 0.25rem 0.4rem; }
  .oms-count { color: var(--text-secondary, #8a8b8d); font-size: 0.78rem; }
  .oms-all { border: none; background: transparent; color: var(--color-primary, #00d4ff); cursor: pointer; font-size: 0.78rem; padding: 0; }
  .oms-option {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.4rem; border-radius: 6px; cursor: pointer;
    color: var(--text-primary, #ffffff);
  }
  .oms-option:hover { background: var(--bg-tertiary, #1a1b1e); }
  .oms-option.selected { font-weight: 600; }
  .oms-option.disabled-row { cursor: not-allowed; opacity: 0.6; }
  .oms-check { width: 1rem; color: var(--color-primary, #00d4ff); }
  .oms-option-label { color: var(--text-primary, #ffffff); }
</style>
